import { isConversationArchiveInbox } from 'dashboard/helper/conversationArchive';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, sortComparatorWithUnreadFirst } from './helpers';
import { buildConversationCacheKey } from '../../../helper/conversationCache';
import { areSameConversationId } from '../../../helper/conversationIdentity';
import { shouldPreserveConversationPageOrder } from '../../../helper/conversationPlacementState';
import { resolveConversationPlacementSortView } from '../../../helper/conversationFrozenPlacement';

export const getSelectedChatConversation = ({
  allConversations = [],
  selectedChat = null,
  selectedChatId = null,
} = {}) => {
  const selectedConversation = allConversations.find((conversation) =>
    areSameConversationId(conversation.id, selectedChatId)
  );

  if (selectedConversation) {
    return [selectedConversation];
  }

  if (selectedChat && areSameConversationId(selectedChat.id, selectedChatId)) {
    return [selectedChat];
  }

  return [];
};

const mergeConversationSnapshots = (primary = {}, secondary = {}) => {
  const merged = { ...secondary, ...primary };

  const primaryLastIncoming =
    primary?.last_incoming_at === undefined
      ? undefined
      : primary.last_incoming_at;
  const secondaryLastIncoming =
    secondary?.last_incoming_at === undefined
      ? undefined
      : secondary.last_incoming_at;

  if (
    (primaryLastIncoming === undefined || primaryLastIncoming === null) &&
    secondaryLastIncoming !== undefined &&
    secondaryLastIncoming !== null
  ) {
    merged.last_incoming_at = secondaryLastIncoming;
  }

  const primaryMessages = Array.isArray(primary?.messages)
    ? primary.messages
    : null;
  const secondaryMessages = Array.isArray(secondary?.messages)
    ? secondary.messages
    : null;

  if (
    (!primaryMessages || primaryMessages.length === 0) &&
    secondaryMessages &&
    secondaryMessages.length
  ) {
    merged.messages = secondaryMessages;
  }

  if (secondary?.meta || primary?.meta) {
    merged.meta = {
      ...(secondary?.meta || {}),
      ...(primary?.meta || {}),
    };
  }

  if (
    (!Array.isArray(primary?.attachments) ||
      primary.attachments.length === 0) &&
    Array.isArray(secondary?.attachments) &&
    secondary.attachments.length
  ) {
    merged.attachments = secondary.attachments;
  }

  return merged;
};

const getComparableTimestamp = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getters = {
  getAllConversations: ({
    allConversations,
    chatSortFilter: sortKey,
    conversationFilters,
    frozenPlacements,
    chatStatusFilter,
    unreadFirstEnabled,
    priorityFilterEnabled,
  }) => {
    const uniqueConversations = allConversations.reduce((acc, current) => {
      const channel =
        current.meta?.channel || current.channel || current.channel_type;
      const status = conversationFilters?.status || chatStatusFilter || 'open';
      if (
        isConversationArchiveInbox({ channel_type: channel }) &&
        status !== 'all' &&
        current.status !== status
      )
        return acc;
      const existingIndex = acc.findIndex((item) =>
        areSameConversationId(item.id, current.id)
      );

      if (existingIndex === -1) {
        return acc.concat([current]);
      }

      const existing = acc[existingIndex];
      const existingActivity = getComparableTimestamp(
        existing.last_activity_at
      );
      const currentActivity = getComparableTimestamp(current.last_activity_at);

      const primary = currentActivity >= existingActivity ? current : existing;
      const secondary = primary === current ? existing : current;
      const merged = mergeConversationSnapshots(primary, secondary);

      const next = acc.slice();
      next.splice(existingIndex, 1, merged);
      return next;
    }, []);

    // Page 1 can be live-sorted, but older pages must keep the server order:
    // otherwise realtime inserts/updates can make page 3 look like today's inbox.
    if (
      shouldPreserveConversationPageOrder({
        currentPage: conversationFilters?.page,
      })
    ) {
      return uniqueConversations;
    }

    const effectiveSortKey = priorityFilterEnabled
      ? 'priority_urgency'
      : sortKey;
    // Conversazioni congelate (Otello al lavoro su una inbox API): il
    // comparatore legge l'istantanea dei campi di ordinamento, la lista
    // restituisce l'oggetto vivo con anteprima ed etichette aggiornate.
    const sortableConversations = uniqueConversations.map((conversation) => ({
      conversation,
      sortView: resolveConversationPlacementSortView({
        conversation,
        frozenPlacements,
      }),
    }));
    sortableConversations.sort((a, b) =>
      sortComparatorWithUnreadFirst(
        a.sortView,
        b.sortView,
        effectiveSortKey,
        unreadFirstEnabled
      )
    );
    return sortableConversations.map(({ conversation }) => conversation);
  },
  getSelectedChat: ({ selectedChatId, selectedChat, allConversations }) => {
    if (!selectedChatId) return {};
    const selectedConversation = allConversations.find((conversation) =>
      areSameConversationId(conversation.id, selectedChatId)
    );
    if (selectedConversation) {
      return selectedConversation;
    }
    if (
      selectedChat &&
      areSameConversationId(selectedChat.id, selectedChatId)
    ) {
      return selectedChat;
    }
    return { id: selectedChatId };
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    return selectedChat.attachments || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getConversationFreshArrivals: ({ freshArrivals }) => freshArrivals || {},
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find((message) => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => (activeFilters) => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter((conversation) => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFilters: (_state) => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: (_state) => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state) => (activeFilters) => {
    return _state.allConversations.filter((conversation) => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: (_state) => (activeFilters) => {
    return _state.allConversations.filter((conversation) => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      (chatMessage) =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getActiveInboxFilter: ({ activeInboxFilter }) => activeInboxFilter,
  getConversationById: (_state) => (conversationId) => {
    const conversation = _state.allConversations.find((value) =>
      areSameConversationId(value.id, conversationId)
    );
    if (conversation) {
      return conversation;
    }
    if (
      _state.selectedChat &&
      areSameConversationId(_state.selectedChat.id, conversationId)
    ) {
      return _state.selectedChat;
    }
    return undefined;
  },
  // La lista visibile e' solo una delle pagine: gli handler realtime devono
  // poter risolvere anche una conversazione che ora vive solo nelle pagine in
  // cache di un'altra cartella.
  getCachedConversationById: (_state) => (conversationId) => {
    const caches = Object.values(_state.conversationCache || {});
    for (let index = 0; index < caches.length; index += 1) {
      const conversations = caches[index]?.conversations;
      const match = Array.isArray(conversations)
        ? conversations.find((conversation) =>
            areSameConversationId(conversation?.id, conversationId)
          )
        : undefined;
      if (match) {
        return match;
      }
    }
    return undefined;
  },
  getConversationParticipants: (_state) => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: (_state) => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: (_state) => {
    return _state.contextMenuChatId;
  },

  getDirectConversations: (state) => {
    return state.allConversations.filter(
      (conversation) => conversation.isDirectConversation
    );
  },

  getTotalConversations: (state) => state.totalConversations,

  getPaginationLoadingStatus: (state) => state.paginationLoading,

  getLastFetchedConversationId: (state) => state.lastFetchedConversationId,

  getHasNewConversations: (state) => state.hasNewConversations,

  getCurrentConversationId: (state) => {
    if (state.selectedChatId) return state.selectedChatId;

    const activeConversation = state.allConversations.find(
      (conv) => conv.status === 'open'
    );
    return activeConversation ? activeConversation.id : null;
  },

  // Getters universali per il cache delle conversazioni
  getConversationCache: (state) => (cacheKey) => {
    return state.conversationCache[cacheKey] || null;
  },

  generateCacheKey: () => (inboxId, filters) => {
    return buildConversationCacheKey(inboxId, filters);
  },

  isConversationCacheValid: (state) => (cacheKey, inboxType) => {
    const cache = state.conversationCache[cacheKey];

    if (!cache) return false;

    const now = Date.now();
    const expiration =
      state.cacheExpirationByType[inboxType] ||
      state.cacheExpirationByType.default;
    const isExpired = now - cache.timestamp > expiration;

    return !isExpired;
  },

  hasConversationCache: (state) => (cacheKey) => {
    return !!state.conversationCache[cacheKey];
  },

  getCacheExpirationForInboxType: (state) => (inboxType) => {
    return (
      state.cacheExpirationByType[inboxType] ||
      state.cacheExpirationByType.default
    );
  },

  // ✨ NUOVO: Statistiche del cache per debugging e monitoraggio performance
  getCacheStatistics: (state) => () => {
    const stats = {
      totalCacheEntries: Object.keys(state.conversationCache).length,
      cacheByInboxType: {},
      cacheByAge: { fresh: 0, medium: 0, old: 0 },
      totalConversationsCached: 0,
      oldestCacheEntry: null,
      newestCacheEntry: null,
    };

    const now = Date.now();
    let oldestTimestamp = Infinity;
    let newestTimestamp = 0;

    Object.entries(state.conversationCache).forEach(([key, cache]) => {
      const { inboxType, timestamp, conversations } = cache;
      const age = now - timestamp;

      // Statistiche per tipo di inbox
      if (!stats.cacheByInboxType[inboxType]) {
        stats.cacheByInboxType[inboxType] = {
          count: 0,
          totalConversations: 0,
          avgAge: 0,
          totalAge: 0,
        };
      }

      stats.cacheByInboxType[inboxType].count += 1;
      stats.cacheByInboxType[inboxType].totalConversations +=
        conversations?.length || 0;
      stats.cacheByInboxType[inboxType].totalAge += age;

      // Statistiche per età
      const fiveMinutes = 5 * 60 * 1000;
      const fifteenMinutes = 15 * 60 * 1000;

      if (age < fiveMinutes) {
        stats.cacheByAge.fresh += 1;
      } else if (age < fifteenMinutes) {
        stats.cacheByAge.medium += 1;
      } else {
        stats.cacheByAge.old += 1;
      }

      // Tracking oldest/newest
      if (timestamp < oldestTimestamp) {
        oldestTimestamp = timestamp;
        stats.oldestCacheEntry = { key, age, inboxType };
      }
      if (timestamp > newestTimestamp) {
        newestTimestamp = timestamp;
        stats.newestCacheEntry = { key, age, inboxType };
      }

      stats.totalConversationsCached += conversations?.length || 0;
    });

    // Calcola medie
    Object.values(stats.cacheByInboxType).forEach((typeStats) => {
      if (typeStats.count > 0) {
        typeStats.avgAge = Math.round(typeStats.totalAge / typeStats.count);
        delete typeStats.totalAge;
      }
    });

    return stats;
  },

  // ✨ NUOVO: Verifica salute del cache
  getCacheHealth: (state) => () => {
    const now = Date.now();
    const health = {
      status: 'healthy',
      issues: [],
      recommendations: [],
      efficiency: 0,
    };

    let validEntries = 0;
    const totalEntries = Object.keys(state.conversationCache).length;

    Object.entries(state.conversationCache).forEach(([key, cache]) => {
      const { inboxType, timestamp } = cache;
      const age = now - timestamp;
      const expiration =
        state.cacheExpirationByType[inboxType] ||
        state.cacheExpirationByType.default;

      if (age < expiration) {
        validEntries += 1;
      } else {
        health.issues.push(
          `Cache scaduto per chiave ${key.substring(0, 20)}... (età: ${Math.round(age / 1000)}s)`
        );
      }
    });

    health.efficiency =
      totalEntries > 0 ? (validEntries / totalEntries) * 100 : 100;

    if (health.efficiency < 70) {
      health.status = 'warning';
      health.recommendations.push(
        'Considerare pulizia cache scaduto più frequente'
      );
    }

    if (totalEntries > 50) {
      health.status = 'warning';
      health.recommendations.push(
        'Cache troppo grande, considerare limitazione dimensioni'
      );
    }

    if (health.issues.length > 5) {
      health.status = 'critical';
    }

    return health;
  },
};

export default getters;
